import { Box } from "@mui/material";

import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { stringToList } from "../../../common/helpers/common";
import isEmpty from "../../../common/helpers/isEmpty";
import useIsLoading from "../../../hooks/useIsLoading";
import { NewCreditInvoiceDialog } from "../creditInvoice/NewCreditInvoiceDialog";
import { useExamineInvoice } from "../examineInvoice/useExamineInvoice";
import useInvoicePaging from "../hooks/useInvoicePaging";
import { fetchInvoicesByNumbers, selectAll } from "../invoiceDetailedSlice";
import { isInvoicePartOfCreditInvoiceBundle } from "../utils";
import ConvertToCreditInvoiceDialog from "./ConvertToCreditInvoiceDialog";
import { PreviewInvoiceActionBanner } from "./PreviewInvoiceActionBanner";
import { PreviewInvoiceContent } from "./PreviewInvoiceContent";
import { PreviewInvoiceLanguage } from "./PreviewInvoiceLanguage";

export const PreviewInvoicePage = () => {
  const [
    showCreateNewCreditInvoiceDialog,
    setShowCreateNewCreditInvoiceDialog,
  ] = useState(false);
  const [
    showConvertToCreditInvoiceDialog,
    setShowConvertToCreditInvoiceDialog,
  ] = useState(false);
  const [isRowSelectionEnabled, setIsRowSelectionEnabled] = useState(false);

  const selectedInvoices = useAppSelector((state) => selectAll(state));
  const selectedInvoiceIds = selectedInvoices.map((inv) => inv.id);

  const params = useParams();
  const dispatch = useAppDispatch();

  const { selectedIndex, cycleInvoices } = useInvoicePaging({
    invoicesLength: selectedInvoices.length,
  });
  const selectedInvoice = selectedInvoices[selectedIndex];
  const { openWindow, closeWindow } = useExamineInvoice(selectedInvoice?.id);

  const creditInvoiceBundleInvoices = selectedInvoices.filter((invoice) =>
    isInvoicePartOfCreditInvoiceBundle(invoice),
  );

  const isLoading = useIsLoading(["invoicesDetailed"]);

  const invoiceIdsFromParams = useMemo<string[]>(
    () => stringToList(params.invoiceIds),
    [params.invoiceIds],
  );

  const requiresFetch = useMemo<boolean>(() => {
    if (invoiceIdsFromParams.length === 0) return false;
    if (isEmpty(selectedInvoices)) return true;
    return invoiceIdsFromParams.some((id) => !selectedInvoiceIds.includes(id));
  }, [invoiceIdsFromParams, selectedInvoiceIds, selectedInvoices]);

  useEffect(() => {
    if (requiresFetch) {
      dispatch(fetchInvoicesByNumbers(invoiceIdsFromParams));
    }
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, params.invoiceIds, requiresFetch]);

  const toggleCreateNewCreditInvoiceDialog = () =>
    setShowCreateNewCreditInvoiceDialog(!showCreateNewCreditInvoiceDialog);
  const toggleConvertToCreditInvoiceDialog = () =>
    setShowConvertToCreditInvoiceDialog(!showConvertToCreditInvoiceDialog);

  return (
    <Box>
      <OverlaySpinner isLoading={isLoading} />
      {selectedInvoice && (
        <>
          <PreviewInvoiceLanguage language={selectedInvoice.language} />
          <PreviewInvoiceContent
            invoice={selectedInvoice}
            isRowSelectionEnabled={isRowSelectionEnabled}
            onRowSelect={openWindow}
          />
          <ConvertToCreditInvoiceDialog
            invoiceId={selectedInvoice.id}
            open={showConvertToCreditInvoiceDialog}
            toggleOpen={toggleConvertToCreditInvoiceDialog}
          />
          <NewCreditInvoiceDialog
            selectedInvoiceId={selectedInvoice.id}
            isOpen={showCreateNewCreditInvoiceDialog}
            closeDialog={toggleCreateNewCreditInvoiceDialog}
          />

          <PreviewInvoiceActionBanner
            translation="invoices"
            selectedInvoice={selectedInvoice}
            currentInvoice={selectedIndex + 1}
            numberOfInvoices={selectedInvoices.length}
            cycleInvoices={cycleInvoices}
            creditInvoiceBundleInvoices={creditInvoiceBundleInvoices}
            toggleCreateNewCreditInvoiceDialog={
              toggleCreateNewCreditInvoiceDialog
            }
            toggleConvertToCreditInvoiceDialog={
              toggleConvertToCreditInvoiceDialog
            }
            isRowSelectionEnabled={isRowSelectionEnabled}
            toggleRowSelectionEnabled={() =>
              setIsRowSelectionEnabled((prev) => {
                const next = !prev;
                if (next) {
                  openWindow();
                } else {
                  closeWindow();
                }
                return next;
              })
            }
          />
        </>
      )}
    </Box>
  );
};
